import { formatDateOnlyString, parseDateOnlyString } from "~/pages/DataVault/utils/datetime";
import { EMPTY_ADDRESS } from "../constants";
import type { AddressValues, ControllingPersonEntry } from "../types";
import type {
  Address,
  CrsControllingPersonTypeEnum,
  GetTaxReportingDataQuery,
  TaxReportingProfileMetadataInput,
} from "~/graphql/supergraphTypes";

type TaxReportingDataRow = NonNullable<GetTaxReportingDataQuery["taxReportingData"]>[number];

/**
 * Parses API date strings without timezone shifting calendar days: uses
 * {@link parseDateOnlyString} for YYYY-MM-DD (see DataVault datetime utils), and the same
 * UTC-calendar approach as DocumentDetailsRightSidebar parseDateMetadata for datetimes.
 */
export const parseCalendarDateString = (value: string): Date | null => {
  const dateOnly = parseDateOnlyString(value);
  if (dateOnly) return dateOnly;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

const formatDateForMetadataInput = (d: Date | null | undefined): string | undefined => {
  if (!d) return undefined;
  return formatDateOnlyString(d);
};

const readMetaString = (meta: Record<string, unknown>, ...keys: string[]): string => {
  for (const k of keys) {
    const v = meta[k];
    if (typeof v === "string") return v;
  }
  return "";
};

const parseMetaBool = (value: unknown): boolean | undefined => {
  if (typeof value === "boolean") return value;
  return undefined;
};

const parseMetaDate = (value: unknown): Date | null => {
  if (value == null) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return parseDateOnlyString(formatDateOnlyString(value));
  }
  if (typeof value === "string") {
    return parseCalendarDateString(value);
  }
  return null;
};

const parseMetaAddress = (raw: unknown): AddressValues => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { ...EMPTY_ADDRESS };
  }
  const o = raw as Record<string, unknown>;
  return {
    country: typeof o.country === "string" ? o.country : "",
    streetLine1: typeof o.street === "string" ? o.street : "",
    streetLine2: typeof o.street2 === "string" ? o.street2 : "",
    city: typeof o.city === "string" ? o.city : "",
    stateOrProvince: typeof o.state === "string" ? o.state : "",
    zipCode: typeof o.postalCode === "string" ? o.postalCode : "",
  };
};

const addressValuesToTaxReportingAddress = (addr: AddressValues): Address => ({
  country: addr.country || undefined,
  street: addr.streetLine1 || undefined,
  street2: addr.streetLine2 || undefined,
  city: addr.city || undefined,
  state: addr.stateOrProvince || undefined,
  postalCode: addr.zipCode || undefined,
});

export const controllingPersonEntryToMetadataInput = (
  entry: ControllingPersonEntry,
): TaxReportingProfileMetadataInput => ({
  firstName: entry.firstName || undefined,
  middleName: entry.middleName || undefined,
  lastName: entry.lastName || undefined,
  dob: formatDateForMetadataInput(entry.dateOfBirth ?? null),
  address: addressValuesToTaxReportingAddress(entry.address),
  tin: entry.tin || undefined,
  tinNotRequired: entry.tinNotRequired,
  controllingPersonType:
    (entry.controllingPersonType as CrsControllingPersonTypeEnum) || undefined,
});

const metaRecord = (metaData: unknown): Record<string, unknown> =>
  metaData && typeof metaData === "object" && !Array.isArray(metaData)
    ? (metaData as Record<string, unknown>)
    : {};

const parseTaxReportingMetaToPersonFields = (
  metaData: unknown,
): Omit<
  ControllingPersonEntry,
  "id" | "taxReportingMetadataId" | "pendingSourceProfileId" | "_isDraft"
> => {
  const meta = metaRecord(metaData);
  return {
    firstName: readMetaString(meta, "first_name", "firstName"),
    middleName: readMetaString(meta, "middle_name", "middleName"),
    lastName: readMetaString(meta, "last_name", "lastName"),
    dateOfBirth: parseMetaDate(meta.dob ?? meta.date_of_birth),
    address: parseMetaAddress(meta.address),
    tin: readMetaString(meta, "tin"),
    tinNotRequired: parseMetaBool(meta.tin_not_required ?? meta.tinNotRequired),
    controllingPersonType:
      readMetaString(meta, "controlling_person_type", "controllingPersonType") || undefined,
  };
};

export const taxReportingRowToControllingPersonEntry = (
  row: TaxReportingDataRow,
): ControllingPersonEntry => {
  const parsed = parseTaxReportingMetaToPersonFields(row.metaData);
  return {
    id: `tr-${row.id}`,
    taxReportingMetadataId: row.id,
    ...parsed,
  };
};

export const mergeControllingPersonAfterMutation = (
  draft: ControllingPersonEntry,
  metadataId: number,
  metaData: unknown,
): ControllingPersonEntry => {
  const parsed = parseTaxReportingMetaToPersonFields(metaData);
  const { _isDraft, pendingSourceProfileId: _pending, ...rest } = draft;
  return {
    ...rest,
    ...parsed,
    tin: rest.tin,
    id: `tr-${metadataId}`,
    taxReportingMetadataId: metadataId,
    controllingPersonType: rest.controllingPersonType ?? parsed.controllingPersonType,
  };
};
